import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'

import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {useIssueCreateMetadata} from '../contexts/IssueCreateDataContext'
import {MetadataFooter} from './MetadataFooter'
import {MetadataSidebar} from './MetadataSidebar'

export type SharedMetadataProps = {
  repo: string
  owner: string
  canSetAssignees: boolean
  canSetLabels: boolean
  canSetMilestone: boolean
  canCreateLabel: boolean
  canCreateMilestone: boolean
  canSetProjects: boolean
  canSetType: boolean
  canSetIssueFields: boolean
  canSetRelationships: boolean
  issueTypesEnabled: boolean
  ownerPinnedFields: readonly IssueFieldSelection[] | null
  isPinnedFieldsLoading: boolean
  fieldVisibilityMap: Record<string, string>
  ownerHasIssueFields: boolean
  orgHasIssueTypes: boolean
}

export const MetadataSelectors = ({
  ownerPinnedFields,
  isPinnedFieldsLoading,
  fieldVisibilityMap,
  ownerHasIssueFields,
  orgHasIssueTypes,
}: {
  ownerPinnedFields: readonly IssueFieldSelection[] | null
  isPinnedFieldsLoading: boolean
  fieldVisibilityMap: Record<string, string>
  ownerHasIssueFields: boolean
  orgHasIssueTypes: boolean
}) => {
  const {optionConfig} = useIssueCreateConfigContext()
  const showMetadataInSidebar = !optionConfig.insidePortal
  const {repository} = useIssueCreateMetadata()

  const canSetAssignees = repository?.viewerIssueCreationPermissions?.assignable ?? false
  const canSetLabels = repository?.viewerIssueCreationPermissions?.labelable ?? false
  const canSetMilestone = repository?.viewerIssueCreationPermissions?.milestoneable ?? false
  const viewerCanPush = repository?.viewerCanPush ?? false
  const canSetProjects = repository?.viewerIssueCreationPermissions?.triageable ?? false
  const canSetType = repository?.viewerIssueCreationPermissions?.typeable ?? false
  const canSetIssueFields = repository?.viewerIssueCreationPermissions?.canSetIssueFields ?? false
  const canSetRelationships = repository?.viewerIssueCreationPermissions?.triageable ?? false
  const issueTypesEnabled = repository?.owner?.issueTypesEnabled ?? false

  const sharedMetadataProps: SharedMetadataProps = {
    repo: repository?.name || '',
    owner: repository?.owner.login || '',
    canSetAssignees,
    canSetLabels,
    canSetMilestone,
    canCreateLabel: viewerCanPush,
    canCreateMilestone: viewerCanPush,
    canSetProjects,
    canSetType,
    canSetIssueFields,
    canSetRelationships,
    issueTypesEnabled,
    ownerPinnedFields,
    isPinnedFieldsLoading,
    fieldVisibilityMap,
    ownerHasIssueFields,
    orgHasIssueTypes,
  }

  return showMetadataInSidebar ? (
    <MetadataSidebar {...sharedMetadataProps} />
  ) : (
    <MetadataFooter {...sharedMetadataProps} />
  )
}
